import React, { useEffect, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import { selectUserInfo } from "redux/userInfo/userInfoSlice";
import {
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Grid,
  Typography,
  Box,
  Divider,
} from "components/mui";
import { ContactInput } from "containers/pages/contacts/components";
import * as TY from "containers/pages/users/constants";
import {
  AccessSelector,
  InputPwd,
  RoleGroupSelector,
} from "containers/pages/users/components";
import useUserInfoForm from "./useUserInfoForm.js";
import { array, bool, func, object, string, number } from "prop-types";
import PostLoginAppSelector from "../postLoginAppSelector/PostLoginAppSelector";
import { DeleteIcon } from "components/icons";
import { useFolderPermissionsIntegration } from "./useFolderPermissionsIntegration.jsx";
import { FolderPermissionsButton } from "containers/pages/users/components/folderPermissions";

/**
 * @component FolderPermissionsSection
 * @description Section for folder permissions management
 */
const FolderPermissionsSection = memo(({ 
  mode, 
  userId, 
  draftPermissionsCount, 
  onOpenModal 
}) => (
  <Grid item xs={12}>
    <Box sx={{ mb: 2 }}>
      <FolderPermissionsButton 
        onClick={onOpenModal}
        disabled={false}
      />
      
      {mode === "create" && draftPermissionsCount > 0 && (
        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
          {draftPermissionsCount} folder permissions configured.
        </Typography>
      )}
    </Box>
  </Grid>
));

FolderPermissionsSection.displayName = 'FolderPermissionsSection';

/**
 * @component ActionButtons
 * @description Action buttons for save/reset/delete
 */
const ActionButtons = memo(({ onClickCreateUser, onResetBtn, onDeleteUser, userId }) => (
  <Box
    mt={4}
    display="flex"
    gap={2}
    justifyContent="flex-end"
    sx={{
      boxShadow: 3,
      backgroundColor: "#f5faff",
      borderRadius: 2,
      p: 2,
    }}
  >
    <Button
      variant="contained"
      onClick={onClickCreateUser}
      data-tour="user-save"
      sx={{ minWidth: 120 }}
    >
      SAVE
    </Button>
    
    <Button
      variant="outlined"
      onClick={onResetBtn}
      sx={{ minWidth: 120 }}
    >
      RESET
    </Button>
    
    {onDeleteUser && (
      <Tooltip title="Delete User" placement="top">
        <IconButton
          color="error"
          onClick={() => onDeleteUser(userId)}
          sx={{ ml: 1 }}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    )}
  </Box>
));

ActionButtons.displayName = 'ActionButtons';

/**
 * @component SimpleUserForm
 * @description Simplified user creation form
 */
const SimpleUserForm = memo(({
  user,
  valid,
  filteredGroups,
  fixedGroups,
  handleForm,
  onClickCreateUser,
  onResetBtn,
  onDeleteUser,
  folderPermissionsProps,
  canManageFolderPermissions
}) => (
  <>
    <Box mb={2}>
      <Typography variant="h6" color="text.primary" gutterBottom>
        Account
      </Typography>
      <Divider />
    </Box>
    
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <ContactInput
          name={TY.U_NAME}
          label="Username (Email)"
          type="text"
          required={true}
          value={user[TY.U_NAME]}
          valid={valid[TY.U_NAME]}
          onChange={handleForm}
        />
      </Grid>
      
      <Grid item xs={12} md={6} data-tour="user-password">
        <InputPwd
          name={TY.PWD}
          label="Password"
          required={true}
          value={user[TY.PWD]}
          valid={valid[TY.PWD]}
          onChange={handleForm}
        />
      </Grid>
      
      <Grid item xs={12} md={6} data-tour="user-confirm-password">
        <InputPwd
          name={TY.CONFIRM_PWD}
          label="Confirm Password"
          required={true}
          value={user[TY.CONFIRM_PWD]}
          valid={valid[TY.CONFIRM_PWD]}
          onChange={handleForm}
        />
      </Grid>
      
      <Grid item xs={12}>
        <RoleGroupSelector
          user={user}
          valid={valid}
          options={filteredGroups}
          fixedOptions={fixedGroups}
          handleForm={handleForm}
        />
      </Grid>

      <Grid item xs={12}>
        <AccessSelector
          user={user}
          valid={valid}
          handleForm={handleForm}
        />
      </Grid>

      {canManageFolderPermissions && (
        <FolderPermissionsSection {...folderPermissionsProps} />
      )}
    </Grid>

    <Box my={3}>
      <Divider />
    </Box>

    <ActionButtons
      onClickCreateUser={onClickCreateUser}
      onResetBtn={onResetBtn}
      onDeleteUser={onDeleteUser}
      userId={user[TY.ID]}
    />
  </>
));

SimpleUserForm.displayName = 'SimpleUserForm';

/**
 * @component FullUserForm  
 * @description Complete user management form with all options
 */
const FullUserForm = memo(({
  user,
  valid,
  filteredGroups,
  fixedGroups,
  tabs,
  handleForm,
  onClickCreateUser,
  onResetBtn,
  onDeleteUser,
  folderPermissionsProps,
  canManageFolderPermissions
}) => (
  <>
    <Box mb={2}>
      <Typography variant="h6" color="text.primary" gutterBottom>
        Account
      </Typography>
    </Box>
    
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <ContactInput
          data-tour="user-username"
          name={TY.U_NAME}
          label="Username (Email)"
          type="text"
          required={true}
          value={user[TY.U_NAME]}
          valid={valid[TY.U_NAME]}
          onChange={handleForm}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          label="Account Enabled"
          control={
            <Switch
              name={TY.IS_ENABLED}
              checked={user[TY.IS_ENABLED]}
              onChange={handleForm}
            />
          }
          sx={{ mt: 1 }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <InputPwd
          data-tour="user-password"
          name={TY.PWD}
          label="Password"
          required={true}
          value={user[TY.PWD]}
          valid={valid[TY.PWD]}
          onChange={handleForm}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <InputPwd
          data-tour="user-confirm-password"
          name={TY.CONFIRM_PWD}
          label="Confirm Password"
          required={true}
          value={user[TY.CONFIRM_PWD]}
          valid={valid[TY.CONFIRM_PWD]}
          onChange={handleForm}
        />
      </Grid>
    </Grid>

    <Box my={3}>
      <Divider />
    </Box>
    
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <AccessSelector
          user={user}
          valid={valid}
          handleForm={handleForm}
        />
      </Grid>
      
      <Grid item xs={12} md={6} data-tour="user-role-group">
        <RoleGroupSelector
          user={user}
          valid={valid}
          options={filteredGroups}
          fixedOptions={fixedGroups}
          handleForm={handleForm}
        />
      </Grid>
    </Grid>
    
    <Grid container spacing={3} mt={1} mb={3}>
      <Grid item xs={12} md={12}>
        <PostLoginAppSelector
          user={user}
          options={tabs}
          handleForm={handleForm}
        />
      </Grid>

      {canManageFolderPermissions && (
        <FolderPermissionsSection {...folderPermissionsProps} />
      )}
    </Grid>
    
    <Box>
      <Divider />
    </Box>
    
    <ActionButtons
      onClickCreateUser={onClickCreateUser}
      onResetBtn={onResetBtn}
      onDeleteUser={onDeleteUser}
      userId={user[TY.ID]}
    />
  </>
));

FullUserForm.displayName = 'FullUserForm';

/**
 * @component UserInfoForm
 * @description Main user information form component with folder permissions integration
 * 
 * OPTIMIZATIONS APPLIED:
 * - Split into smaller, focused components (SimpleUserForm, FullUserForm, ActionButtons)
 * - Extracted folder permissions logic into custom hook
 * - Reduced code duplication with reusable FolderPermissionsSection
 * - Memoized components to prevent unnecessary re-renders
 * - Simplified prop interface with grouped folder permissions props
 * - Optimized useEffect dependencies
 * - Better separation of concerns (UI vs logic)
 */
function UserInfoForm(props) {
  const { filteredGroups, fixedGroups, tabs } = useUserInfoForm(
    props.userGroups,
    props.user.roles
  );

  const folderPermissions = useFolderPermissionsIntegration({
    mode: props.mode,
    userId: props.userId,
    onPermissionsSet: props.onFolderPermissionsSet,
    onAutoSave: props.onFolderPermissionsAutoSave
  });

  const options = props.user[TY.ACCESS_OPTIONS];
  const mstCustId = props.user[TY.MST_CUST_ID];

  const handleAccessDataEffect = useCallback(() => {
    if (
      Array.isArray(options) &&
      options.length > 0 &&
      !mstCustId
    ) {
      const fakeEvent = { target: { name: TY.MST_CUST_ID } };
      props.handleForm(fakeEvent, options.length > 1 ? options[1] : options[0]);
    }
  }, [options, mstCustId, props]);

  useEffect(() => {
    handleAccessDataEffect();
  }, [handleAccessDataEffect]);

  // Role-based authorization for folder permissions
  const { userInfo } = useSelector(selectUserInfo);
  const roles = userInfo?.roles || [];
  const canManageFolderPermissions = roles.includes('CANshareV3_Admin') || roles.includes('CANshareV3_Executive');

  const folderPermissionsProps = {
    mode: folderPermissions.mode,
    userId: props.userId,
    draftPermissionsCount: folderPermissions.draftPermissions.length,
    onOpenModal: folderPermissions.handleOpenModal
  };

  // Common props for both form variants
  const commonFormProps = {
    user: props.user,
    valid: props.valid,
    filteredGroups,
    fixedGroups,
    handleForm: props.handleForm,
    onClickCreateUser: props.onClickCreateUser,
    onResetBtn: props.onResetBtn,
    onDeleteUser: props.onDeleteUser,
    folderPermissionsProps,
    canManageFolderPermissions
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          padding: 3,
          margin: 2,
        }}
      >
        {props.complexity === "simple" ? (
          <SimpleUserForm {...commonFormProps} />
        ) : (
          <FullUserForm {...commonFormProps} tabs={tabs} />
        )}
      </Paper>

      {/* Folder Permissions Modal */}
      {folderPermissions.modal}
    </>
  );
}

// Fixed PropTypes typo: propsType → propTypes
UserInfoForm.propTypes = {
  user: object,
  valid: object,
  canSave: bool,
  handleForm: func,
  onResetBtn: func,
  onClickCreateUser: func,
  userGroups: array,
  onDeleteUser: func,
  complexity: string,
  // Folder permissions props
  mode: string,
  userId: number,
  onFolderPermissionsSet: func,
  onFolderPermissionsAutoSave: func,
};

export default memo(UserInfoForm);
